import {
  type ColumnDef,
  createColumnHelper,
  type SortingState,
} from "@tanstack/react-table";
import {
  ArrowDownTrayIcon,
  Cog6ToothIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClipboardDocumentCheckIcon,
} from "@heroicons/react/24/outline";
import ColumnSelectorButton from "@via/components/column-selector-button/column-selector-button";
import { GenericError } from "@via/components/error.tsx";
import { Spinner } from "@via/components/loading/loading.tsx";
import DeleteModal from "@via/components/delete-modal/delete-modal";
import Table, { TableSize } from "@via/components/table/table.tsx";
import {
  DEFAULT_EXCLUDED_VMETA_COLUMNS_SAMPLE_EXPLORER,
  HIDDEN_BY_DEFAULT_COLUMNS_SAMPLE_EXPLORER,
  ERROR,
  ModalSize,
} from "@via/utils/constants.tsx";
import { classNames, formatDateString } from "@via/utils/helpers.tsx";

import {
  type JSX,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FieldType as AdvancedFilterFieldType,
  FieldTypeEnum,
} from "@via/components/advanced-filter/helpers/types";
import { getFieldTypeFromString } from "@via/components/advanced-filter/helpers/utils";
import { formatFieldValue } from "@via/pages/sample-trackers/samples/utils/field-helpers.tsx";
import {
  FileQueryFilter,
  FilterLogic,
  MetadataFilter,
  PipelineFilter,
  type SampleExplorerRunExportMode,
} from "@via/pages/sample-trackers/samples/types/filters.ts";
import {
  deserializeFileQueryFilters,
  deserializeMetadataFilters,
  deserializePipelineFilters,
  serializeFileQueryFilters,
  serializeMetadataFilters,
} from "@via/pages/sample-trackers/samples/utils/view-state.ts";
import {
  SampleData,
  useCreateSampleExplorerView,
  useDeleteSampleExplorerView,
  useExportSampleExplorer,
  useSampleExplorer,
  useSampleExplorerViews,
  useUpdateSampleExplorerView,
  useCanvasFields,
} from "@via/pages/sample-trackers/samples/sample-explorer.api.tsx";
import FilterSection from "@via/pages/sample-trackers/samples/components/filter-section.tsx";
import SampleRowExpandedContent from "@via/pages/sample-trackers/samples/components/sample-row-expanded-content.tsx";
import SaveSampleExplorerViewModal from "@via/pages/sample-trackers/samples/components/save-sample-explorer-view-modal.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import { isSingleSelectOption } from "@via/components/select/utils";
import Modal from "@via/components/modal";
import { useAuth } from "@via/hooks/useAuth";
import { useVmetaCanvasOwner } from "@via/hooks/useVmetaCanvasOwner";
import {
  useVmetaCollections,
  useVmetaConfigApis,
} from "@via/pages/projects/metadata/metadata.api.tsx";
import { useVmetaCanvas } from "@via/pages/projects/projects.api.tsx";
import { USER_ROLES } from "@via/utils/constants.tsx";
import { SelectOption } from "@via/utils/interfaces.tsx";
import { getRunStatusText } from "@via/pages/projects/runs/utils/helpers.tsx";
import { Link } from "react-router-dom";
import {
  normalizeSampleExplorerViewState,
  type SampleExplorerView,
  type SampleExplorerViewState,
  type VmetaExplorerVisibleColumn,
} from "common";

const columnHelper = createColumnHelper<SampleData>();

const SIMPLE_SEARCH_FIELD_TYPES = new Set<FieldTypeEnum>([
  FieldTypeEnum.String,
  FieldTypeEnum.Array,
  FieldTypeEnum.Mixed,
]);

const RUN_REPORTS_COLUMN_ID_PREFIX = "run_reports_";
const CANVAS_FIELD_COLUMN_ID_PREFIX = "canvas_field_";
const FIELD_COLUMN_ID_PREFIX = "field_";
const DEFAULT_EXPORT_RUN_MODE: SampleExplorerRunExportMode = "separate_sheet";

const areFieldListsEqual = (
  a: AdvancedFilterFieldType[],
  b: AdvancedFilterFieldType[]
): boolean => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (x.name !== y.name) return false;
    if (x.label !== y.label) return false;
    if (x.type !== y.type) return false;
    if (x.hasNamingPattern !== y.hasNamingPattern) return false;
  }
  return true;
};

const areStringArraysEqual = (a: string[], b: string[]): boolean => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
};

const mergeOrderedFieldNames = (
  availableFieldNames: string[],
  preferredFieldNames: string[]
): string[] => {
  const availableFieldNameSet = new Set(availableFieldNames);
  const orderedFieldNames = preferredFieldNames.filter(
    (fieldName, index) =>
      availableFieldNameSet.has(fieldName) &&
      preferredFieldNames.indexOf(fieldName) === index
  );
  const orderedFieldNameSet = new Set(orderedFieldNames);

  for (const fieldName of availableFieldNames) {
    if (!orderedFieldNameSet.has(fieldName)) {
      orderedFieldNames.push(fieldName);
    }
  }

  return orderedFieldNames;
};

interface SampleExplorerProps {
  projectId?: string;
  canvasIds?: string[];
  defaultPageSize?: number;
  showSavedViewsControls?: boolean;
  // The sample query is keyed on canvasIds and stays disabled until the caller has
  // worked out which trackers are in scope. A disabled query reports isLoading false,
  // so without this the table would render its "no data" message for the whole wait.
  isScopeLoading?: boolean;
  trackerSelection?: {
    label?: string;
    placeholder?: string;
    options: SelectOption[];
    value: SelectOption[];
    onChange: (opts: SelectOption[]) => void;
    isLoading?: boolean;
    onFetchMoreOptions?: () => void;
    formatOptionLabel?: (
      option: SelectOption,
      ctx: { context: "menu" | "value" }
    ) => ReactNode;
  };
  adminTrackerSelection?: {
    label?: string;
    placeholder?: string;
    helperText?: string;
    options: SelectOption[];
    value: SelectOption[];
    onChange: (opts: SelectOption[]) => void;
    isLoading?: boolean;
    onFetchMoreOptions?: () => void;
    formatOptionLabel?: (
      option: SelectOption,
      ctx: { context: "menu" | "value" }
    ) => ReactNode;
  };
}

export default function SampleExplorer({
  projectId,
  canvasIds,
  defaultPageSize = 20,
  showSavedViewsControls = true,
  isScopeLoading = false,
  trackerSelection,
  adminTrackerSelection,
}: SampleExplorerProps): JSX.Element {
  const { user } = useAuth();
  const tableViewportRef = useRef<HTMLDivElement | null>(null);
  const defaultViewCanvasRef = useRef<string | null>(null);
  const pendingUnsavedInitializationCanvasRef = useRef<string | null>(null);
  const visibleFieldsInitializedRef = useRef(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [simpleQueryDraft, setSimpleQueryDraft] = useState("");
  const [simpleQuery, setSimpleQuery] = useState("");
  const [simpleSearchFields, setSimpleSearchFields] = useState<
    AdvancedFilterFieldType[]
  >([]);
  const [expandedViewportWidth, setExpandedViewportWidth] = useState<
    number | undefined
  >(undefined);
  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  // Sorting state
  const [sort, setSort] = useState<SortingState>([]);
  // Ref to track previous runReportFields to avoid clearing sort during data updates
  const prevRunReportFieldsRef = useRef<
    Record<string, { name: string; type: "Number" }[]> | undefined
  >(undefined);
  // Filters apply immediately on add/edit/remove/logic change
  const [metadataFilters, setMetadataFilters] = useState<MetadataFilter[]>([]);
  const [metadataLogic, setMetadataLogic] = useState<FilterLogic>("OR");
  const [pipelineFilters, setPipelineFilters] = useState<PipelineFilter[]>([]);
  const [pipelineLogic, setPipelineLogic] = useState<FilterLogic>("OR");
  const [fileQueryFilters, setFileQueryFilters] = useState<FileQueryFilter[]>(
    []
  );
  const [fileQueryLogic, setFileQueryLogic] = useState<FilterLogic>("OR");
  const [formatRoute, setFormatRoute] = useState("");
  const [visibleFieldNames, setVisibleFieldNames] = useState<string[]>([]);
  const [orderedFieldNames, setOrderedFieldNames] = useState<string[]>([]);
  const [isTrackersModalOpen, setIsTrackersModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportRunMode, setExportRunMode] =
    useState<SampleExplorerRunExportMode>(DEFAULT_EXPORT_RUN_MODE);
  const [selectedViewId, setSelectedViewId] = useState<number | null>(null);
  const [isSaveViewModalOpen, setIsSaveViewModalOpen] = useState(false);
  const [viewToDelete, setViewToDelete] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const effectiveCanvasIds = useMemo(() => {
    if (Array.isArray(canvasIds) && canvasIds.length > 0) return canvasIds;
    if (projectId) return [projectId];
    return [];
  }, [canvasIds, projectId]);

  const singleCanvasId =
    effectiveCanvasIds.length === 1 ? effectiveCanvasIds[0] : undefined;
  const { data: canvasData, isLoading: isCanvasLoading } = useVmetaCanvas(
    showSavedViewsControls ? (singleCanvasId ?? "") : ""
  );
  const isAdmin = user && user.userRole === USER_ROLES.ADMIN;
  const canvasOwner = canvasData?.data?.owner;
  const isOwner = useVmetaCanvasOwner(canvasOwner);
  const canManageSavedViews =
    !singleCanvasId || isAdmin || !!canvasData?.data?.writeAllowed || isOwner;
  const isReadOnlySavedViewsMode =
    showSavedViewsControls &&
    !!singleCanvasId &&
    !isCanvasLoading &&
    !canManageSavedViews;
  const savedViewsCanvasId = showSavedViewsControls
    ? (singleCanvasId ?? "")
    : "";
  const savedViewsQuery = useSampleExplorerViews(savedViewsCanvasId);
  const createViewMutation = useCreateSampleExplorerView(savedViewsCanvasId);
  const updateViewMutation = useUpdateSampleExplorerView(savedViewsCanvasId);
  const deleteViewMutation = useDeleteSampleExplorerView(savedViewsCanvasId);
  const savedViews = savedViewsQuery.data ?? [];
  const selectedSavedView =
    savedViews.find(view => view.id === selectedViewId) ?? null;
  const { data: fileCollections } = useVmetaCollections(
    0,
    100,
    [],
    [
      { id: "projectID", value: singleCanvasId ?? "" },
      { id: "name", value: "file" },
    ]
  );
  const { data: configApis, isLoading: isConfigApisLoading } =
    useVmetaConfigApis(singleCanvasId ?? "", 0, 100);

  const fileCollectionId = useMemo(() => {
    return (
      fileCollections?.data?.find(collection => collection.name === "file")
        ?._id ?? null
    );
  }, [fileCollections?.data]);

  const formatRouteOptions = useMemo<SelectOption[]>(() => {
    const filteredConfigApis = (configApis?.data ?? []).filter(configApi =>
      fileCollectionId ? configApi.collectionID === fileCollectionId : true
    );
    const routes = Array.from(
      new Set(filteredConfigApis.map(configApi => configApi.route))
    ).sort((a, b) => a.localeCompare(b));

    return routes.map(route => ({
      label: route,
      value: route,
    }));
  }, [configApis?.data, fileCollectionId]);

  // Fetch field types for filter dropdown (fallback)
  const { data: fallbackFields = [] } = useCanvasFields(singleCanvasId || "");

  // Convert SortingState to API format
  const convertSortToApiFormat = useMemo(() => {
    if (sort.length === 0) return { sort: undefined, order: undefined };
    const firstSort = sort[0];
    const columnId = firstSort.id;

    // Handle run_reports_<reportRelativePath>::<fieldName> format (for runReports columns)
    if (columnId.startsWith(RUN_REPORTS_COLUMN_ID_PREFIX)) {
      const parts = columnId
        .replace(RUN_REPORTS_COLUMN_ID_PREFIX, "")
        .split("::");
      if (parts.length === 2) {
        const [reportRelativePath, fieldName] = parts;
        return {
          sort: {
            category: "runReports" as const,
            reportRelativePath: reportRelativePath,
            column: fieldName,
          },
          order: firstSort.desc ? ("desc" as const) : ("asc" as const),
        };
      }
    }

    // Handle canvas_field_<fieldName> format (for grouped Sample Tracker Fields)
    if (columnId.startsWith(CANVAS_FIELD_COLUMN_ID_PREFIX)) {
      const fieldName = columnId.replace(CANVAS_FIELD_COLUMN_ID_PREFIX, "");
      return {
        sort: { category: "canvasFields" as const, column: fieldName },
        order: firstSort.desc ? ("desc" as const) : ("asc" as const),
      };
    }

    // Handle field_<fieldName> format (for regular canvasFields columns)
    if (columnId.startsWith(FIELD_COLUMN_ID_PREFIX)) {
      const fieldName = columnId.replace(FIELD_COLUMN_ID_PREFIX, "");
      return {
        sort: { category: "canvasFields" as const, column: fieldName },
        order: firstSort.desc ? ("desc" as const) : ("asc" as const),
      };
    }

    return { sort: undefined, order: undefined };
  }, [sort]);

  // Extract sort and order for API call
  const apiSort = convertSortToApiFormat.sort;
  const apiOrder = convertSortToApiFormat.order;

  const simpleSearchableFields = useMemo(() => {
    return [...simpleSearchFields]
      .filter(f => {
        if (!f?.name || typeof f.name !== "string" || f.name.trim() === "") {
          return false;
        }
        return (
          SIMPLE_SEARCH_FIELD_TYPES.has(f.type) &&
          !HIDDEN_BY_DEFAULT_COLUMNS_SAMPLE_EXPLORER.has(f.name)
        );
      })
      .sort((a, b) => {
        const an = String(a?.name ?? "");
        const bn = String(b?.name ?? "");
        return an.localeCompare(bn);
      });
  }, [simpleSearchFields]);

  const simpleSearchMetadataFilters = useMemo((): MetadataFilter[] => {
    const q = simpleQuery.trim();
    if (!q) return [];
    return simpleSearchableFields.map(f => ({
      field: f.name,
      operator: "contains",
      value: q,
    }));
  }, [simpleQuery, simpleSearchableFields]);

  const effectiveMetadataFilters = isAdvancedOpen
    ? metadataFilters
    : simpleSearchMetadataFilters;
  const effectiveMetadataLogic: FilterLogic = isAdvancedOpen
    ? metadataLogic
    : "OR";

  const effectivePipelineFilters = isAdvancedOpen ? pipelineFilters : [];
  const effectivePipelineLogic: FilterLogic = isAdvancedOpen
    ? pipelineLogic
    : "OR";

  const effectiveFileQueryFilters = isAdvancedOpen ? fileQueryFilters : [];
  const effectiveFileQueryLogic: FilterLogic = isAdvancedOpen
    ? fileQueryLogic
    : "OR";

  const handleSubmitSimpleSearch = (): void => {
    setSimpleQuery(simpleQueryDraft);
    setPage(0);
  };

  const handleOpenAdvanced = (): void => {
    setIsAdvancedOpen(true);
    setSimpleQuery("");
    setSimpleQueryDraft("");
    setPage(0);
  };

  const handleCloseAdvanced = (): void => {
    setIsAdvancedOpen(false);
    setPage(0);
  };

  const reportsRequireSingleTracker =
    isAdvancedOpen &&
    fileQueryFilters.length > 0 &&
    effectiveCanvasIds.length !== 1;

  const reportsDisabledReason =
    effectiveCanvasIds.length !== 1
      ? "Reports filter requires selecting exactly one Sample Tracker."
      : undefined;

  const explorerQuery = useSampleExplorer({
    canvasIds: effectiveCanvasIds,
    format: formatRoute || undefined,
    metadataFilters: effectiveMetadataFilters,
    metadataLogic: effectiveMetadataLogic,
    pipelineFilters: effectivePipelineFilters,
    pipelineLogic: effectivePipelineLogic,
    // If reports filter is configured but multiple trackers are selected, ignore report filters.
    fileQueryFilters: reportsRequireSingleTracker
      ? []
      : effectiveFileQueryFilters,
    fileQueryLogic: reportsRequireSingleTracker
      ? "OR"
      : effectiveFileQueryLogic,
    take: pageSize,
    skip: page * pageSize,
    sort: apiSort,
    order: apiOrder,
  });
  const exportSampleExplorer = useExportSampleExplorer();
  const { isLoading, isError, data, error } = explorerQuery;

  // Keep simple-search field list aligned with what's shown in Metadata dropdown.
  // Prefer explorer's field_types; fall back to useCanvasFields.
  // IMPORTANT: Update idempotently to avoid update-depth loops when fallbackFields
  // changes identity between renders.
  useEffect(() => {
    // Avoid a feedback loop: simple search query depends on simpleSearchFields,
    // but simpleSearchFields is derived from explorer results. Freeze the field
    // list while a simple search is active so query keys stay stable.
    if (!isAdvancedOpen && simpleQuery.trim() !== "") return;

    let next: AdvancedFilterFieldType[] = [];
    if (data?.meta?.field_types && data.meta.field_types.length > 0) {
      next = data.meta.field_types
        .filter(
          field => !HIDDEN_BY_DEFAULT_COLUMNS_SAMPLE_EXPLORER.has(field.name)
        )
        .map(field => ({
          name: field.name,
          label: field.label,
          type: getFieldTypeFromString(field.type),
          hasNamingPattern: field.hasNamingPattern,
        }));
    } else if (fallbackFields.length > 0) {
      next = fallbackFields;
    }

    setSimpleSearchFields(prev =>
      areFieldListsEqual(prev, next) ? prev : next
    );
  }, [data?.meta?.field_types, fallbackFields, isAdvancedOpen, simpleQuery]);

  // Transform fields from explorer API response to AdvancedFilterFieldType format
  // Use fields from explorer response as primary source, fallback to useCanvasFields if not available
  const fields: AdvancedFilterFieldType[] = useMemo(() => {
    // Prioritize fields from explorer API response
    if (data?.meta?.field_types && data.meta.field_types.length > 0) {
      // Sample Explorer specific: hide system/file bookkeeping fields in Metadata dropdown
      return data.meta.field_types
        .filter(
          field => !HIDDEN_BY_DEFAULT_COLUMNS_SAMPLE_EXPLORER.has(field.name)
        )
        .map(field => ({
          name: field.name,
          label: field.label,
          type: getFieldTypeFromString(field.type),
          hasNamingPattern: field.hasNamingPattern,
        }));
    }
    // Fallback to fields from separate API call
    return fallbackFields;
  }, [data?.meta?.field_types, fallbackFields]);

  // Get Sample Tracker Fields - all fields that are not in DEFAULT_EXCLUDED_VMETA_COLUMNS
  // This dynamically includes any custom fields added to the sample tracker
  const sampleTrackerFields = useMemo(() => {
    if (!data?.meta?.field_types) return [];
    return data.meta.field_types.filter(
      field =>
        !DEFAULT_EXCLUDED_VMETA_COLUMNS_SAMPLE_EXPLORER.includes(field.name)
    );
  }, [data?.meta?.field_types]);

  const availableFieldNames = useMemo(
    () => sampleTrackerFields.map(field => field.name),
    [sampleTrackerFields]
  );

  useEffect(() => {
    if (availableFieldNames.length === 0) {
      return;
    }

    setOrderedFieldNames(prev => {
      const nextOrderedFieldNames = mergeOrderedFieldNames(
        availableFieldNames,
        prev.length > 0 ? prev : availableFieldNames
      );
      return areStringArraysEqual(prev, nextOrderedFieldNames)
        ? prev
        : nextOrderedFieldNames;
    });

    setVisibleFieldNames(prev => {
      if (!visibleFieldsInitializedRef.current) {
        visibleFieldsInitializedRef.current = true;
        return availableFieldNames;
      }

      const nextFieldNameSet = new Set(availableFieldNames);
      const filtered = prev.filter(fieldName =>
        nextFieldNameSet.has(fieldName)
      );
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [availableFieldNames]);

  const orderedSampleTrackerFields = useMemo(() => {
    const sampleTrackerFieldsByName = new Map(
      sampleTrackerFields.map(field => [field.name, field] as const)
    );

    return mergeOrderedFieldNames(
      availableFieldNames,
      orderedFieldNames
    ).flatMap(fieldName => {
      const field = sampleTrackerFieldsByName.get(fieldName);
      return field ? [field] : [];
    });
  }, [availableFieldNames, orderedFieldNames, sampleTrackerFields]);

  const configurableColumnItems = useMemo(
    () =>
      orderedSampleTrackerFields.map(field => ({
        id: field.name,
        label: field.label,
      })),
    [orderedSampleTrackerFields]
  );

  const configurableColumnVisibility = useMemo(
    () =>
      Object.fromEntries(
        configurableColumnItems.map(column => [
          column.id,
          visibleFieldNames.includes(column.id),
        ])
      ),
    [configurableColumnItems, visibleFieldNames]
  );

  const tableColumnVisibility = useMemo(
    () =>
      Object.fromEntries(
        configurableColumnItems.map(column => [
          `${CANVAS_FIELD_COLUMN_ID_PREFIX}${column.id}`,
          visibleFieldNames.includes(column.id),
        ])
      ),
    [configurableColumnItems, visibleFieldNames]
  );

  const defaultVisibleFieldNames = useMemo(
    () =>
      availableFieldNames.filter(
        fieldName => !HIDDEN_BY_DEFAULT_COLUMNS_SAMPLE_EXPLORER.has(fieldName)
      ),
    [availableFieldNames]
  );

  const defaultOrderedFieldNames = useMemo(
    () => availableFieldNames,
    [availableFieldNames]
  );

  const selectedDefaultSort = useMemo(() => {
    const firstSort = sort[0];
    if (!firstSort || !firstSort.id.startsWith(CANVAS_FIELD_COLUMN_ID_PREFIX)) {
      return {
        fieldName: "",
        direction: "asc" as const,
      };
    }

    return {
      fieldName: firstSort.id.replace(CANVAS_FIELD_COLUMN_ID_PREFIX, ""),
      direction: firstSort.desc ? ("desc" as const) : ("asc" as const),
    };
  }, [sort]);

  const applyUnsavedViewDefaults = useCallback((): void => {
    visibleFieldsInitializedRef.current = defaultVisibleFieldNames.length > 0;
    if (defaultVisibleFieldNames.length > 0) {
      setVisibleFieldNames(defaultVisibleFieldNames);
    }
    if (defaultOrderedFieldNames.length > 0) {
      setOrderedFieldNames(defaultOrderedFieldNames);
    }
    setSelectedViewId(null);
  }, [defaultOrderedFieldNames, defaultVisibleFieldNames]);

  // Reset pagination to page 0 when sort changes
  useEffect(() => {
    setPage(0);
  }, [sort]); // Only reset when sort changes, not when page changes

  useEffect(() => {
    const el = tableViewportRef.current;
    if (!el) return;

    const updateWidth = (): void => {
      const next = Math.max(0, Math.floor(el.clientWidth));
      setExpandedViewportWidth(prev => (prev === next ? prev : next));
    };

    updateWidth();

    const observer = new ResizeObserver(() => {
      updateWidth();
    });
    observer.observe(el);
    window.addEventListener("resize", updateWidth);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateWidth);
    };
  }, []);

  // Clear sort state if sorted column is removed from available fields
  // Use ref to track previous state and only clear when column is actually removed
  useEffect(() => {
    if (sort.length === 0) {
      // Update ref when sort is cleared
      if (data?.meta?.runReportFields) {
        prevRunReportFieldsRef.current = data.meta.runReportFields;
      }
      return;
    }

    const sortedColumnId = sort[0].id;

    // Handle runReports columns
    if (sortedColumnId.startsWith(RUN_REPORTS_COLUMN_ID_PREFIX)) {
      const parts = sortedColumnId
        .replace(RUN_REPORTS_COLUMN_ID_PREFIX, "")
        .split("::");
      if (parts.length === 2) {
        const [reportRelativePath, fieldName] = parts;
        const runReportFields = data?.meta?.runReportFields;
        const prevRunReportFields = prevRunReportFieldsRef.current;

        // If we don't have data yet, just update the ref and return
        if (!data) {
          if (runReportFields) {
            prevRunReportFieldsRef.current = runReportFields;
          }
          return;
        }

        // Only clear if we had runReportFields before and now they're completely gone
        // This means the columns were actually removed (e.g., filter change)
        if (
          prevRunReportFields &&
          Object.keys(prevRunReportFields).length > 0 &&
          !runReportFields
        ) {
          setSort([]);
          prevRunReportFieldsRef.current = runReportFields;
          return;
        }

        // If we have runReportFields, verify the field still exists
        if (runReportFields) {
          const reportFields = runReportFields[reportRelativePath];

          // Only clear if we had this field before and now it's gone
          if (prevRunReportFields) {
            const prevReportFields = prevRunReportFields[reportRelativePath];
            if (
              prevReportFields &&
              prevReportFields.some(f => f.name === fieldName)
            ) {
              if (
                !reportFields ||
                !reportFields.some(f => f.name === fieldName)
              ) {
                setSort([]);
              }
            }
          }

          // Update ref for next comparison
          prevRunReportFieldsRef.current = runReportFields;
        } else if (prevRunReportFields) {
          // If we had runReportFields before but don't now, and we have data, clear sort
          setSort([]);
          prevRunReportFieldsRef.current = runReportFields;
        }
      }
      return;
    }

    // Handle canvasFields columns
    // Only check if we have data loaded (to avoid clearing during initial load)
    if (!data?.meta?.field_types) return;

    let fieldName: string | null = null;
    if (sortedColumnId.startsWith(CANVAS_FIELD_COLUMN_ID_PREFIX)) {
      fieldName = sortedColumnId.replace(CANVAS_FIELD_COLUMN_ID_PREFIX, "");
    } else if (sortedColumnId.startsWith(FIELD_COLUMN_ID_PREFIX)) {
      fieldName = sortedColumnId.replace(FIELD_COLUMN_ID_PREFIX, "");
    }

    // Check if the field exists in the actual field types data
    if (fieldName) {
      const fieldExists = data.meta.field_types.some(f => f.name === fieldName);
      if (!fieldExists) {
        setSort([]);
      }
    }
  }, [sort, data?.meta?.field_types, data?.meta?.runReportFields, data]);

  useEffect(() => {
    if (!showSavedViewsControls) {
      defaultViewCanvasRef.current = null;
      pendingUnsavedInitializationCanvasRef.current = null;
      setFormatRoute("");

      if (
        defaultVisibleFieldNames.length > 0 &&
        !visibleFieldsInitializedRef.current
      ) {
        applyUnsavedViewDefaults();
      } else if (selectedViewId !== null) {
        setSelectedViewId(null);
      }
      return;
    }

    if (!singleCanvasId) {
      defaultViewCanvasRef.current = null;
      pendingUnsavedInitializationCanvasRef.current = null;
      visibleFieldsInitializedRef.current = false;
      setVisibleFieldNames([]);
      setOrderedFieldNames([]);
      setFormatRoute("");
      setSelectedViewId(null);
      return;
    }

    if (defaultViewCanvasRef.current !== singleCanvasId) {
      pendingUnsavedInitializationCanvasRef.current = singleCanvasId;
    }

    if (savedViewsQuery.isLoading) {
      return;
    }

    const defaultView = savedViews.find(view => view.isDefault) ?? null;

    if (defaultViewCanvasRef.current === singleCanvasId) {
      return;
    }

    setFormatRoute("");
    if (defaultView) {
      defaultViewCanvasRef.current = singleCanvasId;
      pendingUnsavedInitializationCanvasRef.current = null;
      applySavedViewState(defaultView.viewState, defaultView.id);
    } else {
      if (
        defaultVisibleFieldNames.length > 0 &&
        pendingUnsavedInitializationCanvasRef.current === singleCanvasId
      ) {
        defaultViewCanvasRef.current = singleCanvasId;
        pendingUnsavedInitializationCanvasRef.current = null;
        applyUnsavedViewDefaults();
      } else {
        visibleFieldsInitializedRef.current = false;
        setSelectedViewId(null);
      }
    }
  }, [
    applyUnsavedViewDefaults,
    defaultVisibleFieldNames,
    savedViews,
    savedViewsQuery.isLoading,
    selectedViewId,
    showSavedViewsControls,
    singleCanvasId,
  ]);

  useEffect(() => {
    if (selectedViewId === null) {
      return;
    }

    if (!savedViews.some(view => view.id === selectedViewId)) {
      setSelectedViewId(null);
    }
  }, [savedViews, selectedViewId]);

  const savedViewOptions = useMemo<SelectOption[]>(() => {
    const baseOptions = savedViews.map(view => ({
      label: view.isDefault ? `${view.label} (default)` : view.label,
      value: String(view.id),
    }));

    return [
      {
        label: isReadOnlySavedViewsMode
          ? "Default View"
          : "Current View (Unsaved)",
        value: "",
      },
      ...baseOptions,
    ];
  }, [isReadOnlySavedViewsMode, savedViews]);

  const selectedSavedViewOption =
    savedViewOptions.find(
      option => option.value === String(selectedViewId ?? "")
    ) ?? savedViewOptions[0];

  const handleMetadataFiltersChange = (
    newFilters: MetadataFilter[],
    logic: FilterLogic
  ): void => {
    setMetadataFilters(newFilters);
    setMetadataLogic(logic);
    setPage(0);
  };

  const handlePipelineFiltersChange = (
    newFilters: PipelineFilter[],
    logic: FilterLogic
  ): void => {
    setPipelineFilters(newFilters);
    setPipelineLogic(logic);
    setPage(0);
  };

  const handleFileQueryFiltersChange = (
    newFilters: FileQueryFilter[],
    logic: FilterLogic
  ): void => {
    setFileQueryFilters(newFilters);
    setFileQueryLogic(logic);
    setPage(0);
  };

  const handleClearFilters = (): void => {
    setMetadataFilters([]);
    setMetadataLogic("OR");
    setPipelineFilters([]);
    setPipelineLogic("OR");
    setFileQueryFilters([]);
    setFileQueryLogic("OR");
    // Reset to first page when clearing filters
    setPage(0);
  };

  const buildCurrentViewState = (
    nextFormatRoute?: string
  ): SampleExplorerViewState => {
    const viewState: SampleExplorerViewState = {
      version: 1,
      mode: isAdvancedOpen ? "advanced" : "simple",
      simpleQuery,
      metadata: {
        logic: metadataLogic,
        filters: serializeMetadataFilters(metadataFilters),
      },
      pipeline: {
        logic: pipelineLogic,
        filters: pipelineFilters.map(filter => ({
          field: filter.field,
          operator: filter.operator,
          value: filter.value,
        })),
      },
      reports: {
        logic: fileQueryLogic,
        filters: serializeFileQueryFilters(fileQueryFilters),
      },
      columns: {
        visibleFieldNames,
        orderedFieldNames,
      },
      sorting: sort.map(({ id, desc }) => ({ id, desc })),
    };

    if (nextFormatRoute) {
      viewState.formatRoute = nextFormatRoute;
    }

    return viewState;
  };

  const applySavedViewState = (
    viewState: SampleExplorerViewState,
    viewId: number | null
  ): void => {
    const normalizedViewState = normalizeSampleExplorerViewState(viewState);

    setIsAdvancedOpen(normalizedViewState.mode === "advanced");
    setSimpleQuery(normalizedViewState.simpleQuery);
    setSimpleQueryDraft(normalizedViewState.simpleQuery);
    setMetadataFilters(
      deserializeMetadataFilters(normalizedViewState.metadata.filters)
    );
    setMetadataLogic(normalizedViewState.metadata.logic);
    setPipelineFilters(
      deserializePipelineFilters(normalizedViewState.pipeline.filters)
    );
    setPipelineLogic(normalizedViewState.pipeline.logic);
    setFileQueryFilters(
      deserializeFileQueryFilters(normalizedViewState.reports.filters)
    );
    setFileQueryLogic(normalizedViewState.reports.logic);
    setFormatRoute(normalizedViewState.formatRoute ?? "");
    visibleFieldsInitializedRef.current = true;
    setVisibleFieldNames(normalizedViewState.columns?.visibleFieldNames ?? []);
    setOrderedFieldNames(
      normalizedViewState.columns?.orderedFieldNames ?? availableFieldNames
    );
    setSort(normalizedViewState.sorting.map(sortItem => ({ ...sortItem })));
    setExpandedRows(new Set());
    setPage(0);
    setSelectedViewId(viewId);
  };

  const handleApplySavedView = (view: SampleExplorerView | null): void => {
    if (!view) {
      applyUnsavedViewDefaults();
      return;
    }

    applySavedViewState(view.viewState, view.id);
  };

  const handleOpenManageSavedView = (): void => {
    if (!singleCanvasId) {
      return;
    }

    setIsSaveViewModalOpen(true);
  };

  const handleShowAllColumns = (): void => {
    setVisibleFieldNames(
      mergeOrderedFieldNames(availableFieldNames, orderedFieldNames)
    );
  };

  const handleHideAllColumns = (): void => {
    setVisibleFieldNames([]);
  };

  const handleToggleColumnVisibility = (
    fieldName: string,
    isVisible: boolean
  ): void => {
    setVisibleFieldNames(prev => {
      if (isVisible) {
        return prev.includes(fieldName) ? prev : [...prev, fieldName];
      }

      return prev.filter(currentFieldName => currentFieldName !== fieldName);
    });
  };

  const handleReorderColumns = (nextOrderedFieldNames: string[]): void => {
    setOrderedFieldNames(
      mergeOrderedFieldNames(availableFieldNames, nextOrderedFieldNames)
    );
  };

  const handleDefaultSortFieldChange = (fieldName: string): void => {
    if (!fieldName) {
      setSort([]);
      return;
    }

    setSort([
      {
        id: `${CANVAS_FIELD_COLUMN_ID_PREFIX}${fieldName}`,
        desc: selectedDefaultSort.direction === "desc",
      },
    ]);
  };

  const handleDefaultSortDirectionChange = (
    direction: "asc" | "desc"
  ): void => {
    if (!selectedDefaultSort.fieldName) {
      return;
    }

    setSort([
      {
        id: `${CANVAS_FIELD_COLUMN_ID_PREFIX}${selectedDefaultSort.fieldName}`,
        desc: direction === "desc",
      },
    ]);
  };

  const handleCreateSavedView = async ({
    label,
    isDefault,
    formatRoute: nextFormatRoute,
  }: {
    label: string;
    isDefault: boolean;
    formatRoute?: string;
  }): Promise<void> => {
    const viewState = buildCurrentViewState(nextFormatRoute);
    const createdView = await createViewMutation.mutateAsync({
      label,
      isDefault,
      viewState,
    });

    applySavedViewState(createdView.viewState, createdView.id);
    setIsSaveViewModalOpen(false);
  };

  const handleUpdateSavedView = async ({
    label,
    isDefault,
    formatRoute: nextFormatRoute,
  }: {
    label: string;
    isDefault: boolean;
    formatRoute?: string;
  }): Promise<void> => {
    if (!selectedSavedView) {
      return;
    }

    const viewState = buildCurrentViewState(nextFormatRoute);
    const updatedView = await updateViewMutation.mutateAsync({
      viewId: selectedSavedView.id,
      body: {
        label,
        isDefault,
        viewState,
      },
    });

    applySavedViewState(updatedView.viewState, updatedView.id);
    setIsSaveViewModalOpen(false);
  };

  const handleDeleteSavedView = async (): Promise<void> => {
    if (!viewToDelete) {
      return;
    }

    await deleteViewMutation.mutateAsync(viewToDelete.id);
    setViewToDelete(null);
    setSelectedViewId(null);
    setIsSaveViewModalOpen(false);
  };

  const handleExpandedRowsChange = (newExpandedRows: Set<string>): void => {
    setExpandedRows(newExpandedRows);
  };

  const toggleRowExpansion = (sampleId: string) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(sampleId)) {
      newExpandedRows.delete(sampleId);
    } else {
      newExpandedRows.add(sampleId);
    }
    handleExpandedRowsChange(newExpandedRows);
  };

  // Dynamic Run Reports columns grouped by reportRelativePath
  const runReportsColumns = useMemo(() => {
    const runReportFields = data?.meta?.runReportFields;

    // If no runReportFields, don't show Reports columns
    if (!runReportFields || Object.keys(runReportFields).length === 0) {
      return [];
    }

    // Create one grouped column per reportRelativePath with one leaf column per field.
    // This ensures headers align perfectly with their cell values.
    return Object.entries(runReportFields).map(([reportRelativePath, fields]) =>
      columnHelper.group({
        id: `reports_${reportRelativePath}`,
        header: () => (
          <div className="flex flex-col">
            <span>Reports</span>
            <span className="mt-0.5 text-xs font-normal text-gray-400">
              {reportRelativePath}
            </span>
          </div>
        ),
        columns: fields.map(field =>
          columnHelper.accessor(
            row => (row as any).runReports?.[reportRelativePath]?.[field.name],
            {
              id: `${RUN_REPORTS_COLUMN_ID_PREFIX}${reportRelativePath}::${field.name}`,
              header: () => <span>{field.name}</span>,
              cell: ({ getValue }) => (
                <>{formatFieldValue(getValue(), field.type)}</>
              ),
              enableSorting: true,
              size: 140,
              enableResizing: true,
              meta: { truncate: true, tooltip: true },
            }
          )
        ),
      })
    );
  }, [data?.meta?.runReportFields]);

  const showCanvasLabelColumn = effectiveCanvasIds.length > 1;

  const columns = [
    columnHelper.display({
      id: "expand",
      header: () => null,
      cell: ({ row }) => {
        const sample = row.original;
        return (
          <button
            onClick={e => {
              e.stopPropagation();
              toggleRowExpansion(sample._id);
            }}
            className="p-1 hover:bg-gray-100 rounded">
            {expandedRows.has(sample._id) ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
          </button>
        );
      },
      size: 40,
      enableSorting: false,
      enableResizing: false,
    }),
    ...(showCanvasLabelColumn
      ? [
          columnHelper.accessor(row => row.canvas_label ?? "", {
            id: "canvas_label",
            header: () => <span>Sample Tracker</span>,
            cell: ({ getValue, row }) => {
              const v = getValue();
              const canvasId = row.original.canvasId;
              const label = v && v.trim() !== "" ? v : "-";

              if (!canvasId || label === "-") return label;

              return (
                <Link
                  to={`/sample-trackers/${canvasId}`}
                  className="via-link"
                  target="_blank"
                  rel="noreferrer">
                  {label}
                </Link>
              );
            },
            enableSorting: false,
            enableResizing: true,
            meta: { truncate: true, tooltip: true },
          }),
        ]
      : []),
    ...(orderedSampleTrackerFields.length > 0
      ? orderedSampleTrackerFields.map(field =>
          columnHelper.accessor(row => (row as any)[field.name], {
            id: `${CANVAS_FIELD_COLUMN_ID_PREFIX}${field.name}`,
            header: () => <span>{field.label}</span>,
            cell: ({ getValue }) => {
              const value = getValue();
              return <>{formatFieldValue(value, field.type)}</>;
            },
            enableSorting: true,
            size: 180,
            enableResizing: true,
            meta: { truncate: true, tooltip: true },
          })
        )
      : []),
    columnHelper.display({
      id: "pipeline_summary",
      header: () => <span>Run</span>,
      cell: ({ row }) => {
        const sample = row.original;
        const { pipelineName, pipelineVersion, runCount } = sample.runSummary;

        const counts = new Map<string, number>();
        for (const r of sample.runData ?? []) {
          const t = getRunStatusText(r.runStatus);
          const label = t ? String(t) : "";
          if (!label) continue;
          counts.set(label, (counts.get(label) ?? 0) + 1);
        }
        const statusSummary =
          counts.size > 0
            ? Array.from(counts.entries())
                .map(([k, v]) => `${k}: ${v}`)
                .join(", ")
            : "-";

        return (
          <div>
            <div className="flex items-center gap-1">{pipelineName}</div>
            <div className="text-xs text-gray-500 mt-1">
              {pipelineVersion} • {runCount} runs
            </div>
            <div className="text-xs text-gray-600 mt-1">{statusSummary}</div>
          </div>
        );
      },
    }),
    // Dynamic Run Reports columns
    ...runReportsColumns,
    columnHelper.accessor("updatedAt", {
      header: () => <span>Updated</span>,
      cell: ({ getValue }) => {
        const value = getValue();
        return (
          <div className="flex items-center gap-1 text-gray-500">
            <span>{formatDateString(value, "LL")}</span>
          </div>
        );
      },
      enableSorting: true,
      enableResizing: true,
      meta: { truncate: true, tooltip: true },
    }),
  ] as Array<ColumnDef<SampleData>>;

  const visibleExportColumns = useMemo((): VmetaExplorerVisibleColumn[] => {
    const visibleColumns: VmetaExplorerVisibleColumn[] = [];
    const visibleFieldNameSet = new Set(visibleFieldNames);

    if (showCanvasLabelColumn) {
      visibleColumns.push({ kind: "system", name: "canvas_label" });
    }

    for (const field of orderedSampleTrackerFields) {
      if (visibleFieldNameSet.has(field.name)) {
        visibleColumns.push({ kind: "field", name: field.name });
      }
    }

    for (const [reportRelativePath, fields] of Object.entries(
      data?.meta?.runReportFields ?? {}
    )) {
      for (const field of fields) {
        visibleColumns.push({
          kind: "runReport",
          reportRelativePath,
          name: field.name,
        });
      }
    }

    visibleColumns.push({ kind: "system", name: "updatedAt" });
    return visibleColumns;
  }, [
    data?.meta?.runReportFields,
    orderedSampleTrackerFields,
    showCanvasLabelColumn,
    visibleFieldNames,
  ]);

  const handleExport = (): void => {
    if (effectiveCanvasIds.length === 0 || exportSampleExplorer.isPending)
      return;

    setExportRunMode(DEFAULT_EXPORT_RUN_MODE);
    setIsExportModalOpen(true);
  };

  const handleConfirmExport = async (): Promise<boolean> => {
    if (effectiveCanvasIds.length === 0 || exportSampleExplorer.isPending) {
      // Nothing was exported, so the modal stays open rather than closing on a confirm that did
      // nothing and leaving the user to guess whether a download is coming. (#450)
      return false;
    }

    await exportSampleExplorer.mutateAsync({
      canvasIds: effectiveCanvasIds,
      format: formatRoute || undefined,
      visibleColumns: visibleExportColumns,
      exportRunMode,
      metadataFilters: effectiveMetadataFilters,
      metadataLogic: effectiveMetadataLogic,
      pipelineFilters: effectivePipelineFilters,
      pipelineLogic: effectivePipelineLogic,
      fileQueryFilters: reportsRequireSingleTracker
        ? []
        : effectiveFileQueryFilters,
      fileQueryLogic: reportsRequireSingleTracker
        ? "OR"
        : effectiveFileQueryLogic,
      sort: apiSort,
      order: apiOrder,
      fileName:
        effectiveCanvasIds.length > 1
          ? "sample-explorer.xlsx"
          : "sample-tracker-samples.xlsx",
    });
    // Reached only once mutateAsync resolves. A rejection propagates, and Modal keeps the modal
    // open for it rather than closing on a failed export.
    return true;
  };

  const exportButton = (
    <button
      type="button"
      onClick={handleExport}
      disabled={
        effectiveCanvasIds.length === 0 || exportSampleExplorer.isPending
      }
      className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed">
      {exportSampleExplorer.isPending ? (
        <Spinner loading className="h-5 w-5" />
      ) : (
        <ArrowDownTrayIcon className="h-5 w-5" />
      )}
      Export
    </button>
  );

  const savedViewsControls =
    showSavedViewsControls && singleCanvasId ? (
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-500 whitespace-nowrap">
          View:
        </span>
        <div className="w-70">
          <SelectComponent
            name="sample-explorer-saved-views"
            options={savedViewOptions}
            value={selectedSavedViewOption}
            readOnly={savedViewsQuery.isLoading}
            isLoading={savedViewsQuery.isLoading}
            placeholder="Select saved view"
            onChange={selected => {
              const selectedOption = isSingleSelectOption(selected)
                ? selected
                : null;
              const value = selectedOption?.value ?? "";
              if (!value) {
                handleApplySavedView(null);
                return;
              }

              const nextView =
                savedViews.find(view => String(view.id) === value) ?? null;
              handleApplySavedView(nextView);
            }}
          />
        </div>

        {!isReadOnlySavedViewsMode && (
          <button
            type="button"
            onClick={handleOpenManageSavedView}
            className="inline-flex items-center rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition disabled:cursor-not-allowed disabled:opacity-50">
            <Cog6ToothIcon className="mr-2 h-5 w-5" />
            Manage view
          </button>
        )}
      </div>
    ) : null;

  return (
    <div>
      <div className="my-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div className="inline-flex shrink-0 overflow-hidden rounded-md border border-gray-300 bg-white shadow-sm">
            <button
              type="button"
              onClick={handleCloseAdvanced}
              className={classNames(
                "px-4 py-2 text-sm font-medium transition",
                !isAdvancedOpen
                  ? "bg-gray-100 text-gray-900"
                  : "text-gray-600 hover:bg-gray-50"
              )}>
              Simple
            </button>
            <button
              type="button"
              onClick={handleOpenAdvanced}
              className={classNames(
                "border-l border-gray-300 px-4 py-2 text-sm font-medium transition",
                isAdvancedOpen
                  ? "bg-gray-100 text-gray-900"
                  : "text-gray-600 hover:bg-gray-50"
              )}>
              Advanced
            </button>
          </div>

          {!isAdvancedOpen ? (
            <form
              className="min-w-0 flex flex-1 items-center gap-2"
              onSubmit={e => {
                e.preventDefault();
                handleSubmitSimpleSearch();
              }}>
              <label className="sr-only" htmlFor="simple-search">
                Search
              </label>
              <div className="min-w-0 flex-1">
                <input
                  id="simple-search"
                  type="text"
                  value={simpleQueryDraft}
                  onChange={e => setSimpleQueryDraft(e.target.value)}
                  placeholder="Search samples..."
                  className="w-full rounded-md border-0 px-3 py-2 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600"
                />
              </div>
              <button
                type="submit"
                className="inline-flex shrink-0 items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2">
                Search
              </button>
            </form>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          {savedViewsControls}
          {exportButton}
        </div>
      </div>

      {showSavedViewsControls &&
        selectedSavedView?.warnings &&
        selectedSavedView.warnings.length > 0 && (
          <div className="mb-4 rounded-lg border border-yellow-300 bg-yellow-50 p-4 text-sm text-yellow-800">
            {selectedSavedView.warnings
              .map(warning => warning.message)
              .join(" ")}
          </div>
        )}

      {showSavedViewsControls && savedViewsQuery.isError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {savedViewsQuery.error?.message ??
            "We couldn't load the saved sample explorer views."}
        </div>
      )}

      <Modal
        open={isExportModalOpen}
        setOpen={setIsExportModalOpen}
        title="Export Data"
        subTitle="Choose how to include run data in your export."
        confirmText="Export"
        onConfirm={handleConfirmExport}
        isConfirmLoading={exportSampleExplorer.isPending}
        size={ModalSize.Small}>
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => setExportRunMode("separate_sheet")}
            className={classNames(
              "w-full rounded-lg border px-4 py-3 text-left transition",
              exportRunMode === "separate_sheet"
                ? "border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500"
                : "border-gray-300 bg-white hover:bg-gray-50"
            )}>
            <div className="text-sm font-medium text-gray-900">
              Separate sheet
            </div>
            <div className="mt-1 text-sm text-gray-600">
              Creates a second sheet with one row per sample-run pair.
            </div>
          </button>

          <button
            type="button"
            onClick={() => setExportRunMode("merge_into_sample_table")}
            className={classNames(
              "w-full rounded-lg border px-4 py-3 text-left transition",
              exportRunMode === "merge_into_sample_table"
                ? "border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500"
                : "border-gray-300 bg-white hover:bg-gray-50"
            )}>
            <div className="text-sm font-medium text-gray-900">
              Combined sheet
            </div>
            <div className="mt-1 text-sm text-gray-600">
              Adds run details as extra columns on the sample sheet.
            </div>
          </button>
        </div>
      </Modal>

      {isAdvancedOpen && (
        <div>
          {(trackerSelection || adminTrackerSelection) && (
            <Modal
              open={isTrackersModalOpen}
              setOpen={setIsTrackersModalOpen}
              title="Sample Trackers"
              showButtons={false}
              size={ModalSize.Large}>
              <div className="space-y-4">
                {trackerSelection && (
                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-2">
                      {trackerSelection.label ?? "Sample Trackers"}
                    </div>
                    <SelectComponent
                      name="sample-trackers"
                      isMulti
                      enableClear={true}
                      options={trackerSelection.options}
                      value={trackerSelection.value}
                      isLoading={trackerSelection.isLoading}
                      onFetchMoreOptions={trackerSelection.onFetchMoreOptions}
                      formatOptionLabel={trackerSelection.formatOptionLabel}
                      placeholder={
                        trackerSelection.placeholder ?? "Select Sample Trackers"
                      }
                      onChange={selected => {
                        trackerSelection.onChange(
                          (Array.isArray(selected)
                            ? selected
                            : []) as SelectOption[]
                        );
                      }}
                    />
                  </div>
                )}

                {adminTrackerSelection && (
                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-2">
                      {adminTrackerSelection.label ??
                        "Admin: Add other trackers"}
                    </div>
                    {adminTrackerSelection.helperText && (
                      <div className="text-xs text-gray-500 mb-2">
                        {adminTrackerSelection.helperText}
                      </div>
                    )}
                    <SelectComponent
                      name="admin-sample-trackers"
                      isMulti
                      enableClear={true}
                      options={adminTrackerSelection.options}
                      value={adminTrackerSelection.value}
                      isLoading={adminTrackerSelection.isLoading}
                      onFetchMoreOptions={
                        adminTrackerSelection.onFetchMoreOptions
                      }
                      formatOptionLabel={
                        adminTrackerSelection.formatOptionLabel
                      }
                      placeholder={
                        adminTrackerSelection.placeholder ??
                        "Select additional Sample Trackers"
                      }
                      onChange={selected => {
                        adminTrackerSelection.onChange(
                          (Array.isArray(selected)
                            ? selected
                            : []) as SelectOption[]
                        );
                      }}
                    />
                  </div>
                )}
              </div>
            </Modal>
          )}

          {reportsRequireSingleTracker && (
            <div className="my-4 border border-yellow-300 rounded-lg bg-yellow-50 p-4 text-sm text-yellow-800">
              Reports filter is configured. Select exactly one Sample Tracker to
              apply it (or remove the Reports filter).
            </div>
          )}

          <FilterSection
            metadataFilters={metadataFilters}
            metadataLogic={metadataLogic}
            onMetadataFiltersChange={handleMetadataFiltersChange}
            pipelineFilters={pipelineFilters}
            pipelineLogic={pipelineLogic}
            onPipelineFiltersChange={handlePipelineFiltersChange}
            fileQueryFilters={fileQueryFilters}
            fileQueryLogic={fileQueryLogic}
            onFileQueryFiltersChange={handleFileQueryFiltersChange}
            onClearFilters={handleClearFilters}
            fields={fields}
            projectId={singleCanvasId}
            disableReports={effectiveCanvasIds.length !== 1}
            reportsDisabledReason={reportsDisabledReason}
            extraFilterButtons={
              trackerSelection || adminTrackerSelection ? (
                <button
                  type="button"
                  onClick={() => setIsTrackersModalOpen(true)}
                  className="inline-flex items-center gap-2 pl-2 pr-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none transition-colors">
                  <ClipboardDocumentCheckIcon className="h-5 w-5" />
                  Sample Trackers
                </button>
              ) : null
            }
          />
        </div>
      )}

      <SaveSampleExplorerViewModal
        isOpen={isSaveViewModalOpen}
        onClose={() => setIsSaveViewModalOpen(false)}
        selectedView={selectedSavedView}
        initialLabel={selectedSavedView?.label ?? ""}
        initialIsDefault={selectedSavedView?.isDefault ?? false}
        formatRoute={formatRoute}
        formatRouteOptions={formatRouteOptions}
        isFormatRoutesLoading={isConfigApisLoading}
        columnItems={configurableColumnItems}
        visibleFieldNames={visibleFieldNames}
        onToggleColumnVisibility={handleToggleColumnVisibility}
        onReorderColumns={handleReorderColumns}
        onShowAllColumns={handleShowAllColumns}
        onHideAllColumns={handleHideAllColumns}
        sortFieldOptions={configurableColumnItems.map(column => ({
          label: column.label,
          value: column.id,
        }))}
        selectedSortField={selectedDefaultSort.fieldName}
        selectedSortDirection={selectedDefaultSort.direction}
        onSortFieldChange={handleDefaultSortFieldChange}
        onSortDirectionChange={handleDefaultSortDirectionChange}
        onFormatRouteChange={setFormatRoute}
        metadataFilters={metadataFilters}
        metadataLogic={metadataLogic}
        pipelineFilters={pipelineFilters}
        pipelineLogic={pipelineLogic}
        fileQueryFilters={fileQueryFilters}
        fileQueryLogic={fileQueryLogic}
        isCreateLoading={createViewMutation.isPending}
        isUpdateLoading={updateViewMutation.isPending}
        isDeleteLoading={deleteViewMutation.isPending}
        onSaveAsNew={handleCreateSavedView}
        onUpdate={handleUpdateSavedView}
        onRequestDelete={() => {
          if (!selectedSavedView) {
            return;
          }

          setViewToDelete({
            id: selectedSavedView.id,
            name: selectedSavedView.label,
          });
        }}
      />

      <DeleteModal
        item={viewToDelete}
        setItem={setViewToDelete}
        deleteFunction={handleDeleteSavedView}
        isLoading={deleteViewMutation.isPending}
        title="Delete Saved View"
        confirmText="Delete Saved View"
      />

      {configurableColumnItems.length > 0 && (
        <div className="mb-4 flex justify-end">
          <ColumnSelectorButton
            columns={configurableColumnItems}
            columnVisibility={configurableColumnVisibility}
            onToggle={handleToggleColumnVisibility}
          />
        </div>
      )}

      {isError ? (
        <div className="via-generic-error">
          <GenericError message={error?.message ?? ERROR} />
        </div>
      ) : (
        <div
          ref={tableViewportRef}
          className="samples-table-container overflow-x-auto">
          <Table
            data={data?.data || []}
            columns={columns}
            total={data?.results}
            size={TableSize.Big}
            showPagination={true}
            enableColumnResizing={true}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            pageSizeOptions={[10, 20, 50, 100]}
            isLoading={isLoading || isScopeLoading}
            getRowId={row => row._id}
            expandedRows={expandedRows}
            onExpandedRowsChange={handleExpandedRowsChange}
            renderExpandedContent={sample => (
              <SampleRowExpandedContent
                sample={sample}
                runReportFields={data?.meta?.runReportFields}
                viewportWidth={expandedViewportWidth}
              />
            )}
            customTableClassName="w-max table-fixed"
            applyColumnSize
            sort={sort}
            setSort={setSort}
            enableSorting={true}
            columnVisibility={tableColumnVisibility}
          />
        </div>
      )}
    </div>
  );
}
